// Supabase client for the 3CHA SPA.
//
// Reads the project URL + anon (public) key from CRA env vars. The anon key is
// PUBLIC by design — Row-Level Security (see supabase/migrations/*_rls.sql) is
// what actually enforces access, keyed on the signed-in role's JWT claim. Never
// put the service_role key in the frontend.
//
// Set these in .env.production (and .env.local for dev):
//   REACT_APP_SUPABASE_URL=https://<ref>.supabase.co
//   REACT_APP_SUPABASE_ANON_KEY=<anon public key>
import { createClient } from '@supabase/supabase-js';
import { getActiveSite } from './sites';

const url = process.env.REACT_APP_SUPABASE_URL;
const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Surfaced loudly in dev; in prod the build should always have these set.
  // eslint-disable-next-line no-console
  console.error(
    '[supabase] Missing REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY. ' +
    'Data calls will fail until these are set.'
  );
}

export const supabase = createClient(url || 'http://localhost', anonKey || 'missing', {
  auth: {
    // Persist the session so a PWA relaunch stays logged in (replaces the old
    // localStorage.3c_token plumbing).
    persistSession: true,
    autoRefreshToken: true,
    storageKey: '3c_supabase_auth',
  },
  global: {
    // Which location (3CHA Kingston / 3CAL Albany) this session is working in.
    // Postgres reads it in auth_site() and every RLS policy scopes to it.
    //
    // 🚨 READ PER REQUEST, NOT AT CONSTRUCTION. It used to be a static header
    // set once on module load, which was correct only while the sole way to
    // change shops was switchSite() — and that reloads. Everything else that
    // can move `3c_site` left the header saying one shop while the UI read the
    // other: another tab or window switching (localStorage is shared and the
    // PWA is routinely left open), an installed PWA resuming, or the auth
    // clamp correcting a stale site. auth.jsx's own comment records the
    // consequence — "every read this page-load would be mis-scoped".
    //
    // That is precisely the reported symptom: the tag at the top says one
    // location and the data is from the other. A function closes the gap by
    // construction; there is no window in which the two can disagree.
    //
    // The header is only a selector; auth_site() ignores it unless the signed
    // JWT's `sites` array already permits that location. So this cannot grant
    // access — it can only stop the client mislabelling what it asked for.
    fetch: (input, init) => {
      const opts = init || {};
      const headers = new Headers(opts.headers || {});
      headers.set('x-3c-site', getActiveSite());
      return fetch(input, { ...opts, headers });
    },
  },
});

export default supabase;
